/**
 * Proof of Grind – productgalerij: zelfde slide bij variantwissel, foto's op tijd laden, vooraf ophalen.
 *
 * 1. Zelfde slide vasthouden: sta je op "in de kamer" en kies je een andere lijst of een ander formaat,
 *    dan opent de nieuwe galerij op "in de kamer" van die variant (niet weer op de hoofdfoto).
 *    Horizon haalt bij een wissel de sectie opnieuw op en vervangt <media-gallery>. Vóór die vervanging
 *    zetten we `initial-slide` in de nieuwe HTML op de slide met hetzelfde soort foto (data-pog-slot).
 * 2. Geen zwarte vlakken: lazy-loading in een horizontaal scrollende galerij laadt een foto soms pas
 *    als hij al half in beeld schuift (je ziet dan een randje foto en verder zwart). Daarom zetten we
 *    alle galerijfoto's op 'eager' zodra de pagina rustig is, zodat ze klaarstaan vóór je erheen gaat.
 * 3. Vooraf ophalen: bij aanwijzen/aanraken van een optieknop halen we de foto op van de variant die je
 *    gaat kiezen, voor de slide waar je nu op staat.
 */
const SLOTS = ['hoofd', 'voor', 'schuin', 'detail', 'alle'];
const WIDTHS = [400, 600, 800, 1000, 1200, 1500];
const loaded = new Set();

function gallery() {
  return document.querySelector('media-gallery.pog-gallery');
}

function variantMap() {
  const el = document.querySelector('script[data-pog-variant-media]');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    return null;
  }
}

/** Soort foto ('hoofd', 'voor', …) van de slide die nu in beeld is. */
function currentSlot() {
  const g = gallery();
  if (!g) return 'hoofd';
  const show = g.querySelector('slideshow-component');
  const slides = [...g.querySelectorAll('slideshow-slide')];
  let index = show && typeof show.current === 'number' ? show.current : -1;
  if (index < 0 || !slides[index]) {
    index = slides.findIndex((s) => s.getAttribute('aria-hidden') === 'false');
  }
  const media = slides[index] && slides[index].querySelector('[data-pog-slot]');
  return (media && media.dataset.pogSlot) || 'hoofd';
}

function targetWidth() {
  const img = document.querySelector('.pog-gallery .pog-gallery__img');
  const cssWidth = img ? img.getBoundingClientRect().width : window.innerWidth;
  const need = cssWidth * (window.devicePixelRatio || 1);
  return WIDTHS.find((w) => w >= need) || WIDTHS[WIDTHS.length - 1];
}

function prefetch(url) {
  if (!url || loaded.has(url)) return;
  loaded.add(url);
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
}

function valuesFor(input) {
  const picker = input.closest('variant-picker') || document;
  const groups = [...picker.querySelectorAll('fieldset')];
  if (!groups.length) return null;
  return groups.map((fs) => {
    if (fs.contains(input)) return input.value;
    const checked = fs.querySelector('input:checked');
    return checked ? checked.value : null;
  });
}

function onIntent(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const label = target.closest('label');
  const input =
    target.closest('input[type="radio"]') ||
    (label && (label.control || label.querySelector('input[type="radio"]')));
  if (!input || !input.closest('variant-picker')) return;
  const map = variantMap();
  const values = valuesFor(input);
  if (!map || !values || values.includes(null)) return;
  const entry = map[values.join('|')];
  if (!entry) return;
  const urls = typeof entry === 'string' ? { hoofd: entry } : entry;
  const url = urls[currentSlot()] || urls.hoofd;
  prefetch(url && url.replace(/width=\d+/, 'width=' + targetWidth()));
}

/** Alle foto's in de galerij laden zodra de browser even niks te doen heeft. */
function warmGallery(root) {
  const run = () => {
    (root || document)
      .querySelectorAll('.pog-gallery img[loading="lazy"]')
      .forEach((img) => {
        img.loading = 'eager';
      });
  };
  if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 1500 });
  else setTimeout(run, 600);
}

/** Zet in de nieuwe galerij-HTML de beginslide op hetzelfde soort foto als nu. Geeft de index terug. */
function keepSlot(html, slot) {
  if (!html || !slot || slot === 'hoofd') return -1;
  const newGallery = html.querySelector('media-gallery.pog-gallery');
  if (!newGallery) return -1;
  const slides = [...newGallery.querySelectorAll('slideshow-slide')];
  const index = slides.findIndex((s) => {
    const m = s.querySelector('[data-pog-slot]');
    return m && m.dataset.pogSlot === slot;
  });
  if (index <= 0) return -1;
  const show = newGallery.querySelector('slideshow-component');
  if (show) show.setAttribute('initial-slide', String(index));
  slides.forEach((s, i) => s.setAttribute('aria-hidden', i === index ? 'false' : 'true'));
  const img = slides[index].querySelector('img');
  if (img) {
    img.loading = 'eager';
    img.setAttribute('fetchpriority', 'high');
  }
  return index;
}

/**
 * Horizon zet de nieuwe galerij wel op `initial-slide`, maar scrolt er bij het vervangen niet altijd
 * naartoe (de breedte is op dat moment nog niet berekend). Daarom selecteren we hem daarna nog eens
 * zelf, zonder animatie, zodra de nieuwe galerij in de pagina staat en een breedte heeft.
 */
function selectWhenReady(index, tries = 12) {
  const show = document.querySelector('media-gallery.pog-gallery slideshow-component');
  const scroller = show && show.querySelector('slideshow-slides');
  if (!show || !scroller || !scroller.clientWidth || typeof show.select !== 'function') {
    if (tries > 0) requestAnimationFrame(() => selectWhenReady(index, tries - 1));
    return;
  }
  const slide = scroller.querySelectorAll('slideshow-slide')[index];
  const expected = slide
    ? slide.getBoundingClientRect().left - scroller.getBoundingClientRect().left + scroller.scrollLeft
    : scroller.clientWidth * index;
  if (Math.abs(scroller.scrollLeft - expected) < 4) return;
  if (show.current === index) {
    // select() doet niets als de index al 'huidig' is (initial-slide), dus zelf scrollen
    scroller.scrollTo({ left: expected, behavior: 'instant' });
  } else {
    show.select(index, undefined, { animate: false });
  }
  // nog één controle in de volgende frame (Horizon kan in dezelfde frame nog terugzetten)
  if (tries > 0) requestAnimationFrame(() => selectWhenReady(index, Math.min(tries - 1, 2)));
}

if (!window.__pogGalleryPrefetch) {
  window.__pogGalleryPrefetch = true;

  const opts = { passive: true, capture: true };
  document.addEventListener('pointerenter', onIntent, opts);
  document.addEventListener('pointerdown', onIntent, opts);
  document.addEventListener('focusin', onIntent, opts);

  // Capture-fase op document: onze .then() hangt vóór die van <media-gallery> aan de promise,
  // dus de nieuwe HTML is aangepast voordat Horizon hem in de pagina zet.
  document.addEventListener(
    'shopify:product:select',
    (event) => {
      if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
      if (!gallery()) return;
      const slot = currentSlot();
      const p = event.promise;
      if (!p || typeof p.then !== 'function') return;
      p.then((result) => {
        const index = keepSlot(result && result.detail && result.detail.html, slot);
        // Horizon's eigen .then() vervangt de galerij hierna; daarna pas selecteren
        setTimeout(() => {
          if (index > 0) selectWhenReady(index);
          warmGallery();
        }, 0);
      }).catch(() => {});
    },
    true
  );

  // Kleurwissel / Met lijst–Alleen poster (pog-navigate.js vervangt <main>): ook dan dezelfde slide houden
  let pendingIndex = -1;
  document.addEventListener('pog:before-swap', (event) => {
    pendingIndex = gallery() ? keepSlot(event.detail && event.detail.html, currentSlot()) : -1;
  });
  document.addEventListener('pog:navigated', () => {
    if (pendingIndex > 0) selectWhenReady(pendingIndex);
    pendingIndex = -1;
    warmGallery();
  });
  if (document.readyState === 'complete') warmGallery();
  else window.addEventListener('load', () => warmGallery(), { once: true });
}
